import { format } from "date-fns";
import type {
  PosEstado,
  PosEstadoDeLaFoto,
  PosEstadoDelAlmacen,
  PosEstadoDelMandato,
  PosParteDeLaFoto,
} from "@/platform/types";
import { formatDecimalString } from "@/utils/formatCurrency";
import { cajerosQueEntran } from "./cajeroDeLaCaja";
import { indicadorDeLaCaja, type Indicador } from "./estadoDeLaCaja";
import { progresoDeLaFoto } from "./fotoDeLaCaja";
import { textoDelTope } from "./topesSinConexion";
import {
  avisoDeLaVentana,
  COLA_ILEGIBLE,
  type NoVendeSinRed,
} from "./ventaSinRed";

/**
 * Lo que muestra el panel de la caja en la barra (#344 AC-2, #345 AC-7, #346):
 * la conexión, la foto parte por parte, si vende sin conexión y por qué no, y
 * quiénes entran con PIN. Nada de esto se esconde: lo que todavía no está, se
 * dice.
 */

const hora = (iso: string) => format(new Date(iso), "dd/MM HH:mm");
const horaConSegundos = (iso: string) => format(new Date(iso), "HH:mm:ss");

export function lineaDeLaConexion(
  estado: Pick<PosEstado, "conexion" | "ultimoLatidoEn" | "sinConexionDesde">
): string {
  switch (estado.conexion) {
    case "conectada":
      return estado.ultimoLatidoEn
        ? `Conectado. Último latido: ${horaConSegundos(estado.ultimoLatidoEn)}.`
        : "Conectado.";
    case "con-problemas":
      return "Con problemas: los latidos fallan o tardan.";
    case "sin-conexion":
      return estado.sinConexionDesde
        ? `Sin conexión desde el ${hora(estado.sinConexionDesde)}.`
        : "Sin conexión.";
    default:
      return "Conectando…";
  }
}

const NOMBRE_DE_LA_PARTE: Record<PosParteDeLaFoto, string> = {
  settings: "Ajustes",
  buyers: "Clientes",
  catalog: "Catálogo",
  prices: "Precios",
  stock: "Stock",
};

const cantidad = (n: number) => n.toLocaleString("es-AR");

export interface FilaDeLaFoto {
  parte: PosParteDeLaFoto;
  nombre: string;
  /** «Al día de dd/MM HH:mm», «Bajando: N cargados» o «Falta». */
  estado: string;
  /**
   * Cuántos tiene la caja de esta parte, `null` si todavía no tiene ninguna.
   * Va aparte del estado para encolumnarla: pegada a la fecha, la corría.
   */
  cantidad: string | null;
  /** Lo que baja encima de la que ya tiene; `null` si no baja nada. */
  bajando: string | null;
}

/** Las cinco partes siempre, aunque la caja todavía no tenga ninguna. */
const ORDEN_DE_LAS_PARTES: PosParteDeLaFoto[] = [
  "catalog",
  "prices",
  "stock",
  "buyers",
  "settings",
];

/** Cada parte de la foto: qué tiene la caja y qué está bajando. */
export function filasDeLaFoto(foto: PosEstadoDeLaFoto): FilaDeLaFoto[] {
  return ORDEN_DE_LAS_PARTES.map((parte) => {
    const p = foto.partes.find((x) => x.parte === parte) ?? {
      parte,
      activa: null,
      bajando: null,
    };
    const nombre = NOMBRE_DE_LA_PARTE[parte];
    const bajando = p.bajando
      ? `Bajando: ${cantidad(p.bajando.items)} cargados`
      : null;
    if (p.activa) {
      return {
        parte: p.parte,
        nombre,
        estado: `Al día de ${hora(p.activa.asOf)}`,
        cantidad: cantidad(p.activa.items),
        bajando,
      };
    }
    return {
      parte: p.parte,
      nombre,
      estado: bajando ?? "Falta",
      cantidad: null,
      bajando: null,
    };
  });
}

/** Qué pasa con la foto, en una línea. */
export function resumenDeLaFoto(
  foto: PosEstadoDeLaFoto,
  servidorConFoto: boolean | null | undefined
): string {
  if (foto.lista) return "Lista: buscar y escanear andan sin conexión.";
  if (foto.sinFoto) {
    return "El servidor todavía no tiene la foto de este negocio: la caja busca con conexión.";
  }
  const progreso = progresoDeLaFoto(foto);
  if (progreso) return progreso;
  if (servidorConFoto === false) {
    return "El servidor todavía no manda la foto: la caja busca y vende con conexión.";
  }
  if (servidorConFoto == null) {
    return "Esperando el primer latido para saber si hay foto.";
  }
  return "Por empezar a bajar.";
}

export interface VentaSinConexion {
  /** Si vende sin conexión ahora. */
  vende: boolean;
  titulo: string;
  lineas: string[];
}

/**
 * Si la caja vende sin conexión, con qué topes y hasta cuándo; si no, por qué
 * (la tabla de §2.7, la misma regla que usa el mostrador).
 */
export function ventaSinConexion(
  motivo: NoVendeSinRed | null,
  mandato: PosEstadoDelMandato | null | undefined,
  ahora = new Date()
): VentaSinConexion {
  const vigente = mandato?.vigente?.mandato;
  if (motivo || !vigente) {
    return {
      vende: false,
      titulo: motivo?.motivo ?? "Sin mandato todavía.",
      lineas: motivo?.detalle ? [motivo.detalle] : [],
    };
  }
  const lineas: string[] = [];
  const aviso = avisoDeLaVentana(mandato, ahora);
  if (aviso) lineas.push(aviso);
  lineas.push(
    `Tope por venta: ${textoDelTope(
      vigente.limits.maxSaleAmount,
      "por-venta"
    )}.`,
    `Tope acumulado: ${textoDelTope(
      vigente.limits.maxTotalAmount,
      "acumulado"
    )}. Vendido sin conexión: ${formatDecimalString(mandato.vendidoSinRed)}.`
  );
  if (mandato.esperandoHoja) {
    lineas.push("Llegó un mandato nuevo: espera su hoja de precios.");
  }
  return {
    vende: true,
    titulo: `Vende sin conexión hasta el ${hora(vigente.hardUntil)}.`,
    lineas,
  };
}

/**
 * Quiénes entran con PIN en este equipo. Con un mandato vigente, sólo los
 * cajeros que éste trae: el PIN de quien ya no es cajero se borra con el
 * próximo latido, y mientras tanto no entra (§2.6, #359).
 */
export function cajerosConPin(
  mandato: PosEstadoDelMandato | null | undefined
): string {
  const lista = mandato?.vigente
    ? cajerosQueEntran(mandato)
    : mandato?.cajerosConPin ?? [];
  const nombres = lista.map((c) => c.nombre);
  return nombres.length > 0
    ? nombres.join(", ")
    : "Nadie cargó su PIN en este equipo.";
}

export function ventasSinSubir(pendientes: number): string {
  if (pendientes === 0) return "No hay ventas sin subir.";
  return pendientes === 1
    ? "1 venta sin subir."
    : `${pendientes} ventas sin subir.`;
}

const PREFIJO_DEL_ALMACEN = "El almacén de la caja no está disponible: ";

export interface AlmacenNoDisponible {
  titulo: string;
  mensaje: string;
  /** El error de Rust, sin el prefijo repetido: para soporte. */
  detalle: string | null;
}

/**
 * Por qué no abrió el almacén de la caja, en palabras del mostrador. Lo más
 * común es el llavero: la clave de los archivos cifrados vive ahí, y si el
 * pedido de acceso se cancela, la caja no abre hasta reabrir la app.
 */
export function almacenNoDisponible(
  error: string | null | undefined,
  sinConexion = false
): AlmacenNoDisponible {
  const detalle = (error ?? "").split(PREFIJO_DEL_ALMACEN).join("").trim();
  // Con red sigue vendiendo online; sin red no hay con qué (§2.7).
  const mientras = sinConexion
    ? COLA_ILEGIBLE
    : "Mientras tanto, este equipo vende online, como la web.";
  if (/llavero/i.test(detalle)) {
    return {
      titulo: /cancel/i.test(detalle)
        ? "Se canceló el permiso del llavero"
        : "El llavero no dio la clave de la caja",
      mensaje: `La caja guarda sus datos cifrados, con una clave en el llavero del sistema. Cerrá la app y volvé a abrirla; cuando pida permiso para el llavero, poné tu contraseña y elegí «Permitir siempre». ${mientras}`,
      detalle: detalle || null,
    };
  }
  return {
    titulo: "La caja de este equipo no está disponible",
    mensaje: `Cerrá la app y volvé a abrirla; si sigue, avisale a soporte. ${mientras}`,
    detalle: detalle || null,
  };
}

/**
 * El chip de la barra: la conexión de la caja, que el almacén no abrió, o que
 * todavía está abriendo. Nunca desaparece sin decir por qué.
 */
export function indicadorDelEquipo(
  estado: PosEstado,
  almacen: PosEstadoDelAlmacen | null,
  abriendo: boolean
): Indicador | null {
  if (almacen && !almacen.disponible) {
    return {
      etiqueta: "Caja no disponible",
      tono: "error",
      detalle: almacenNoDisponible(almacen.error).titulo,
    };
  }
  const indicador = indicadorDeLaCaja(estado);
  if (indicador) return indicador;
  // La cola apartada es del equipo, no de la conexión: sin alta nueva no hay
  // caja que lata, y sin este chip el aviso no se vería (#359).
  if (almacen?.colaApartada) {
    return {
      etiqueta: "Cola apartada",
      tono: "error",
      detalle: "La cola de la caja no se pudo leer y quedó apartada.",
    };
  }
  return abriendo ? { etiqueta: "Abriendo la caja…", tono: "neutro" } : null;
}
